import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  EMPTY,
  REGISTER_ACTION,
  RESPONSE_CODE_406,
} from "../Constants/CommonConstants";
import { REMOVE_VERIFICATION_METHOD_ACTION } from "../Constants/LoginConstants";
import {
  ADD_TOAST,
  EDIT_TOAST,
  EMAIL,
  PHONE,
  PHONE_NO,
  CHANGE_TOAST,
  REMOVE_TOAST,
} from "../Constants/LoginVerificationMethodsConstants";
import { VERIFY_ACTION } from "../Constants/NameAndContactConstants";
import {
  API_INITIAL_STATE,
  BUILDER_TYPE_FULFILLED,
  BUILDER_TYPE_PENDING,
  BUILDER_TYPE_REJECTED,
  SLICE_LOGIN,
  THUNK_GET_ACCOUNT_DETAILS,
  THUNK_GET_VERIFICATION_METHODS,
  THUNK_POST_AUTHENTICATE,
  THUNK_POST_FORGOT_PASSWORD,
  THUNK_POST_FORGOT_USERNAME,
  THUNK_POST_IDENTIFY_USER,
  THUNK_POST_LOGIN_VALIDATE_OTP,
  THUNK_POST_NEW_PASSWORD,
  THUNK_POST_REGISTER_EDIT_VERIFICATION_METHOD,
  THUNK_POST_REMOVE_VERIFICATION_METHOD,
  THUNK_POST_RESET_FORGOT_PASSWORD,
  THUNK_POST_RESET_PASSWORD,
  THUNK_POST_SEND_OTP,
  THUNK_PUT_ACCOUNT,
  THUNK_PUT_SELF_ENABLE,
  THUNK_PUT_VERIFICATION_METHOD,
} from "../Constants/SliceAndThunkConstants";
import {
  MFA_METHOD_REGISTRATION_SUCCESSFUL,
  SEND_OTP_DESCRIPTION,
  USER_ACTION_VERIFY,
} from "../Constants/VerifyAccountConstants";
import { apiSliceBuilderService, parseJwt } from "../Services/CommonServices";
import {
  getAccountDetailsService,
  getVerificationMethodsService,
  postAuthenticateService,
  postForgotPasswordService,
  postForgotUsernameService,
  postIdentifyUserService,
  postLoginValidateOTPService,
  postNewPasswordService,
  postRegisterEditVerificationMethodService,
  postRemoveVerificationMethodService,
  postResetForgotPasswordService,
  postResetPasswordService,
  postSendOTPService,
  putAccountService,
  putSelfEnableService,
  putVerificationMethodService,
} from "../Services/LoginService";

const initialState = {
  loginTokenValues: {
    username: "",
    sub: "",
    iss: "",
    iat: "",
    jti: "",
    nbf: "",
    exp: "",
    sac: "",
    scopes: "",
    pwe: false,
    pwc: false,
    otpr: false,
    otpp: false,
    otpe: false,
    flw: "",
    pvm: "",
    fpactUserType: "",
    lnr: false,
  },
  userName: "",
  dateOfBirth: "",
  loginToken: "",
  flow: "",
  isUserVerificationRemembered: false,
  otpType: "",
  locale: "",
  mockSmsOTP: "",
  authToken: "",
  phoneNo: "",
  email: "",
  monthDayOfBirth: "",
  pwToken: "",
  displayMessage: "",
  sendOTPDescription: "",
  validateOTPDescription: "",
  verificationMethods: {},
  newPasswordApiResponse: "",
  loginResponseDescription: "",
  isUsernameRetrieved: false,
  editOrRegisterMethod: "",
  changePrefMethodDescription: "",
  accountDetailsReceived: false,
  toastFlow: "",
  addEditEmail: "",
  addEditPhone: "",
  isAddEditVerificationFlow: false,
  removeVerificationMethodDescription: "",
  postForgotUsernameResponse: API_INITIAL_STATE,
  postForgotPasswordResponse: API_INITIAL_STATE,
  postNewPasswordResponse: API_INITIAL_STATE,
  postResetForgotPasswordResponse: API_INITIAL_STATE,
  postResetPasswordResponse: API_INITIAL_STATE,
  getVerificationMethodsResponse: API_INITIAL_STATE,
  postSendOTPResponse: API_INITIAL_STATE,
  postLoginValidateOTPResponse: API_INITIAL_STATE,
  postAuthenticateResponse: API_INITIAL_STATE,
  postIdentifyUserResponse: API_INITIAL_STATE,
  postRemoveVerificationMethodResponse: API_INITIAL_STATE,
  postRegisterEditVerificationMethodResponse: API_INITIAL_STATE,
  getAccountDetailsResponse: API_INITIAL_STATE,
  putSelfEnableResponse: API_INITIAL_STATE,
  putAccountResponse: API_INITIAL_STATE,
  putVerificationMethodResponse: API_INITIAL_STATE,
};

export const postAuthenticate = createAsyncThunk(
  `${SLICE_LOGIN}/${THUNK_POST_AUTHENTICATE}`,
  async (payload, { rejectWithValue }) => {
    try {
      return await postAuthenticateService(payload);
    } catch (error) {
      return rejectWithValue(error?.response);
    }
  }
);

export const getAccountDetails = createAsyncThunk(
  `${SLICE_LOGIN}/${THUNK_GET_ACCOUNT_DETAILS}`,
  async () => {
    const response = await getAccountDetailsService();
    return response;
  }
);

export const putSelfEnable = createAsyncThunk(
  `${SLICE_LOGIN}/${THUNK_PUT_SELF_ENABLE}`,
  async () => {
    const response = await putSelfEnableService();
    return response;
  }
);

export const putVerificationMethod = createAsyncThunk(
  `${SLICE_LOGIN}/${THUNK_PUT_VERIFICATION_METHOD}`,
  async (payload) => {
    const response = await putVerificationMethodService(payload);
    return response;
  }
);

export const postForgotUsername = createAsyncThunk(
  `${SLICE_LOGIN}/${THUNK_POST_FORGOT_USERNAME}`,
  async (data, { rejectWithValue }) => {
    try {
      return await postForgotUsernameService(data);
    } catch (error) {
      return rejectWithValue(error?.response);
    }
  }
);

export const getVerificationMethods = createAsyncThunk(
  `${SLICE_LOGIN}/${THUNK_GET_VERIFICATION_METHODS}`,
  async () => {
    const response = await getVerificationMethodsService();
    return response;
  }
);

export const putAccount = createAsyncThunk(
  `${SLICE_LOGIN}/${THUNK_PUT_ACCOUNT}`,
  async (payload) => {
    const response = await putAccountService(payload);
    return response;
  }
);

export const postSendOTP = createAsyncThunk(
  `${SLICE_LOGIN}/${THUNK_POST_SEND_OTP}`,
  async (payload) => {
    const response = await postSendOTPService({
      otpType: payload.otpType,
    });

    return response;
  }
);

export const postLoginValidateOTP = createAsyncThunk(
  `${SLICE_LOGIN}/${THUNK_POST_LOGIN_VALIDATE_OTP}`,
  async (payload, { rejectWithValue }) => {
    try {
      return await postLoginValidateOTPService(payload);
    } catch (error) {
      return rejectWithValue(error?.response);
    }
  }
);

export const postRegisterEditVerificationMethod = createAsyncThunk(
  `${SLICE_LOGIN}/${THUNK_POST_REGISTER_EDIT_VERIFICATION_METHOD}`,
  async (payload, { rejectWithValue }) => {
    try {
      const response = await postRegisterEditVerificationMethodService(
        payload.data,
        payload.apiAction
      );
      return response;
    } catch (error) {
      return rejectWithValue(error?.response);
    }
  }
);

export const postForgotPassword = createAsyncThunk(
  `${SLICE_LOGIN}/${THUNK_POST_FORGOT_PASSWORD}`,
  async ({ data }, { rejectWithValue }) => {
    try {
      return await postForgotPasswordService(data);
    } catch (error) {
      return rejectWithValue(error?.response);
    }
  }
);

export const postNewPassword = createAsyncThunk(
  `${SLICE_LOGIN}/${THUNK_POST_NEW_PASSWORD}`,
  async ({ data, apiAction }, { rejectWithValue }) => {
    try {
      return await postNewPasswordService(data, apiAction);
    } catch (error) {
      return rejectWithValue(error?.response);
    }
  }
);

export const postResetForgotPassword = createAsyncThunk(
  `${SLICE_LOGIN}/${THUNK_POST_RESET_FORGOT_PASSWORD}`,
  async (data, { rejectWithValue }) => {
    try {
      return await postResetForgotPasswordService(data);
    } catch (error) {
      return rejectWithValue(error?.response);
    }
  }
);

export const postResetPassword = createAsyncThunk(
  `${SLICE_LOGIN}/${THUNK_POST_RESET_PASSWORD}`,
  async (data, { rejectWithValue }) => {
    try {
      return await postResetPasswordService(data);
    } catch (error) {
      return rejectWithValue(error?.response);
    }
  }
);

export const postRemoveVerificationMethod = createAsyncThunk(
  `${SLICE_LOGIN}/${THUNK_POST_REMOVE_VERIFICATION_METHOD}`,
  async (payload) => {
    return await postRemoveVerificationMethodService(
      payload,
      REMOVE_VERIFICATION_METHOD_ACTION
    );
  }
);

export const postIdentifyUser = createAsyncThunk(
  `${SLICE_LOGIN}/${THUNK_POST_IDENTIFY_USER}`,
  async (payload, { rejectWithValue }) => {
    try {
      return await postIdentifyUserService(payload, VERIFY_ACTION);
    } catch (error) {
      return rejectWithValue(error?.response);
    }
  }
);

export const loginSlice = createSlice({
  name: SLICE_LOGIN,
  initialState,
  reducers: {
    resetLoginMockSmsOtp: (state) => {
      state.mockSmsOTP = "";
    },
    resetIsUsernameRetrieved: (state) => {
      state.isUsernameRetrieved = false;
    },
    resetLoginValidateOTPDescription: (state) => {
      state.validateOTPDescription = "";
    },
    resetIsUserVerificationRemembered: (state) => {
      state.isUserVerificationRemembered = false;
    },
    resetLoginResponseDescription: (state) => {
      state.loginResponseDescription = "";
    },
    saveForgotUsernameData: (state, action) => {
      state.email = action.payload.email;
      state.dateOfBirth = action.payload.dateOfBirth;
    },
    saveForgotPasswordData: (state, action) => {
      state.userName = action.payload.userName;
      state.monthDayOfBirth = action.payload.monthDayOfBirth;
    },
    saveVerificationMethodsData: (state, action) => {
      state.loginTokenValues.pvm =
        action.payload.data.preferredVerificationMethod;
      state.phoneNo = action?.payload?.data?.telephoneNumber
        ? action?.payload?.data?.telephoneNumber
        : "";
      state.email = action?.payload?.data?.email
        ? action?.payload?.data?.email
        : "";
    },
    updateAddEditVerificationValues: (state, action) => {
      state.addEditEmail = action?.payload?.email
        ? action?.payload?.email
        : state.email;
      state.addEditPhone = action?.payload?.phoneNumber
        ? action?.payload?.phoneNumber
        : state.phoneNo;
    },
    resetAddEditVerificationValues: (state) => {
      state.addEditEmail = "";
      state.addEditPhone = "";
    },
    resetLoginSendOTPDescription: (state) => {
      state.sendOTPDescription = "";
    },
    resetToastFlow: (state) => {
      state.toastFlow = "";
    },
    setIsAddEditVerificationFlow: (state) => {
      state.isAddEditVerificationFlow = true;
    },
    resetIsAddEditVerificationFlow: (state) => {
      state.isAddEditVerificationFlow = false;
    },
    clearLoginApiResponse: (state, action) => {
      const responseName = `${action.payload}Response`;
      if (!initialState[responseName]) {
        return;
      }
      state[responseName] = initialState[responseName];
    },
    clearLoginSlice: () => initialState,
    logout: (state) => {
      state.jwtTokenValues = initialState.jwtTokenValues;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getVerificationMethods.pending, (state) => {
      state.getVerificationMethodsResponse =
        apiSliceBuilderService(BUILDER_TYPE_PENDING);
    });
    builder.addCase(getVerificationMethods.fulfilled, (state, action) => {
      state.getVerificationMethodsResponse = apiSliceBuilderService(
        BUILDER_TYPE_FULFILLED,
        action
      );
      if (action?.payload?.data?.data?.email) {
        state.email = action.payload.data.data.email;
      }
      if (action?.payload?.data?.data?.phoneNum) {
        state.phoneNo = action.payload.data.data.phoneNum;
      }
      if (action?.payload?.data?.data?.loginToken) {
        const decodedToken = parseJwt(action?.payload?.data?.data?.loginToken);
        state.loginToken = action?.payload?.data?.data?.loginToken;
        state.loginTokenValues = decodedToken;
      }
    });

    builder.addCase(putAccount.fulfilled, (state, action) => {
      state.putAccountResponse = apiSliceBuilderService(
        BUILDER_TYPE_FULFILLED,
        action
      );
    });

    builder.addCase(postSendOTP.pending, (state) => {
      state.postSendOTPResponse = apiSliceBuilderService(BUILDER_TYPE_PENDING);
    });
    builder.addCase(postSendOTP.fulfilled, (state, action) => {
      state.postSendOTPResponse = apiSliceBuilderService(
        BUILDER_TYPE_FULFILLED,
        action
      );
      const { authToken = "" } = action.payload.data.data;
      state.sendOTPDescription =
        state?.postSendOTPResponse?.message?.description;
      state.authToken = authToken;
      if (action?.payload?.data?.data?.mockSmsOtp) {
        state.mockSmsOTP = action?.payload?.data?.data?.mockSmsOtp;
      }
      if (action?.payload?.data?.data?.loginToken) {
        const decodedToken = parseJwt(action?.payload?.data?.data?.loginToken);
        state.loginToken = action?.payload?.data?.data?.loginToken;
        state.loginTokenValues = decodedToken;
      }
    });

    builder.addCase(postLoginValidateOTP.pending, (state) => {
      state.postLoginValidateOTPResponse =
        apiSliceBuilderService(BUILDER_TYPE_PENDING);
    });
    builder.addCase(postLoginValidateOTP.rejected, (state, action) => {
      if (action?.payload?.status === RESPONSE_CODE_406) {
        state.postLoginValidateOTPResponse = apiSliceBuilderService(
          BUILDER_TYPE_REJECTED,
          action
        );
      }
      const validateOTPDescription = action?.payload?.data?.error?.message
        ? action?.payload?.data?.error?.message
        : action?.payload?.data?.data?.message?.description;
      state.validateOTPDescription = validateOTPDescription;
      if (action?.payload?.data?.data?.authToken) {
        state.authToken = action?.payload?.data?.data?.authToken;
      }
    });
    builder.addCase(postLoginValidateOTP.fulfilled, (state, action) => {
      state.postLoginValidateOTPResponse = apiSliceBuilderService(
        BUILDER_TYPE_FULFILLED,
        action
      );
      state.validateOTPDescription =
        state.postLoginValidateOTPResponse.message.description;
      if (action?.payload?.data?.data?.loginToken) {
        state.loginToken = action?.payload?.data?.data?.loginToken;
        const decodedToken = parseJwt(action?.payload?.data?.data?.loginToken);
        state.loginTokenValues = decodedToken;
      }
    });
    builder.addCase(postAuthenticate.pending, (state) => {
      state.postAuthenticateResponse =
        apiSliceBuilderService(BUILDER_TYPE_PENDING);
    });
    builder.addCase(postAuthenticate.rejected, (state, action) => {
      state.userName = action.meta.arg.userName;
      if (action?.payload?.status === 406) {
        state.postAuthenticateResponse = apiSliceBuilderService(
          BUILDER_TYPE_REJECTED,
          action
        );
        state.loginResponseDescription = action.payload?.data?.error?.message;
        return;
      }
      throw new Error(action?.payload?.data?.error);
    });
    builder.addCase(postAuthenticate.fulfilled, (state, action) => {
      state.userName = action.meta.arg.userName;
      state.postAuthenticateResponse = apiSliceBuilderService(
        BUILDER_TYPE_FULFILLED,
        action
      );
      const { flow = "", loginToken = "" } = action.payload.data.data;
      const { description } = action.payload.data.message;
      state.flow = flow;
      state.loginToken = loginToken;
      state.loginResponseDescription = description;
      if (loginToken) {
        const decodedLoginToken = parseJwt(loginToken);
        state.loginTokenValues = decodedLoginToken;
      }
    });
    builder.addCase(putVerificationMethod.fulfilled, (state, action) => {
      state.putVerificationMethodResponse = apiSliceBuilderService(
        BUILDER_TYPE_FULFILLED,
        action
      );
      state.toastFlow = CHANGE_TOAST;
      state.loginTokenValues.pvm = action.meta.arg.prefVerificationMethod;
    });

    builder.addCase(postForgotUsername.pending, (state) => {
      state.postForgotUsernameResponse =
        apiSliceBuilderService(BUILDER_TYPE_PENDING);
    });
    builder.addCase(postForgotUsername.rejected, (state, action) => {
      if (action?.payload?.status === RESPONSE_CODE_406) {
        state.postForgotUsernameResponse = apiSliceBuilderService(
          BUILDER_TYPE_REJECTED,
          action
        );
      }
    });
    builder.addCase(postForgotUsername.fulfilled, (state, action) => {
      state.postForgotUsernameResponse = apiSliceBuilderService(
        BUILDER_TYPE_FULFILLED,
        action
      );
      state.isUsernameRetrieved = true;
    });
    builder.addCase(getAccountDetails.pending, (state) => {
      state.getAccountDetailsResponse =
        apiSliceBuilderService(BUILDER_TYPE_PENDING);
    });
    builder.addCase(getAccountDetails.rejected, (state, action) => {
      if (action?.payload?.status === RESPONSE_CODE_406) {
        state.getAccountDetailsResponse = apiSliceBuilderService(
          BUILDER_TYPE_REJECTED,
          action
        );
      }
    });
    builder.addCase(getAccountDetails.fulfilled, (state, action) => {
      state.getAccountDetailsResponse = apiSliceBuilderService(
        BUILDER_TYPE_FULFILLED,
        action
      );
      state.isUserVerificationRemembered =
        action?.payload?.data?.data?.isUserVerificationMethodRemembered;
      state.accountDetailsReceived = true;
    });
    builder.addCase(putSelfEnable.pending, (state) => {
      state.putSelfEnableResponse =
        apiSliceBuilderService(BUILDER_TYPE_PENDING);
    });
    builder.addCase(putSelfEnable.rejected, (state) => {
      state.putSelfEnableResponse = apiSliceBuilderService(
        BUILDER_TYPE_REJECTED
      );
    });
    builder.addCase(putSelfEnable.fulfilled, (state, action) => {
      state.putSelfEnableResponse = apiSliceBuilderService(
        BUILDER_TYPE_FULFILLED,
        action
      );
    });
    builder.addCase(postRegisterEditVerificationMethod.pending, (state) => {
      state.postRegisterEditVerificationMethodResponse =
        apiSliceBuilderService(BUILDER_TYPE_PENDING);
    });
    builder.addCase(
      postRegisterEditVerificationMethod.rejected,
      (state, action) => {
        if (action?.payload?.status === RESPONSE_CODE_406) {
          state.postRegisterEditVerificationMethodResponse =
            apiSliceBuilderService(BUILDER_TYPE_REJECTED, action);

          const validateOTPDescription = action?.payload?.data?.error?.message
            ? action?.payload?.data?.error?.message
            : action?.payload?.data?.data?.message?.description;
          state.validateOTPDescription = validateOTPDescription;
          if (action?.payload?.data?.data?.authToken) {
            state.authToken = action?.payload?.data?.data?.authToken;
          }
        }
      }
    );
    builder.addCase(
      postRegisterEditVerificationMethod.fulfilled,
      (state, action) => {
        state.postRegisterEditVerificationMethodResponse =
          apiSliceBuilderService(BUILDER_TYPE_FULFILLED, action);
        const verificationMethod = action.meta.arg.data.otpType;
        const authToken = action?.payload?.data?.data?.authToken;
        if (authToken) {
          state.authToken = authToken;
        }
        if (action?.meta?.arg?.apiAction === REGISTER_ACTION) {
          state.sendOTPDescription =
            action?.payload?.data?.message?.description;
          sessionStorage.setItem(
            SEND_OTP_DESCRIPTION,
            state.sendOTPDescription
          );
        }
        state.editOrRegisterMethod = verificationMethod;
        if (action?.meta?.arg?.data?.userAction === USER_ACTION_VERIFY) {
          state.validateOTPDescription =
            action?.payload?.data?.message?.description;
          if (
            state.validateOTPDescription === MFA_METHOD_REGISTRATION_SUCCESSFUL
          ) {
            sessionStorage.removeItem(SEND_OTP_DESCRIPTION);
          }
          const initialVerificationMethodValue =
            state[verificationMethod === PHONE ? PHONE_NO : EMAIL];
          state.toastFlow = initialVerificationMethodValue
            ? EDIT_TOAST
            : ADD_TOAST;
          state.email = state.addEditEmail ? state.addEditEmail : state.email;
          state.phoneNo = state.addEditPhone
            ? state.addEditPhone
            : state.phoneNo;
        }

        if (action?.payload?.data?.data?.mockSmsOtp) {
          state.mockSmsOTP = action?.payload?.data?.data?.mockSmsOtp;
        }
      }
    );

    builder.addCase(postForgotPassword.pending, (state) => {
      state.postForgotPasswordResponse =
        apiSliceBuilderService(BUILDER_TYPE_PENDING);
    });
    builder.addCase(postForgotPassword.rejected, (state, action) => {
      if (action?.payload?.status === RESPONSE_CODE_406) {
        state.postForgotPasswordResponse = apiSliceBuilderService(
          BUILDER_TYPE_REJECTED,
          action
        );
      }
    });
    builder.addCase(postForgotPassword.fulfilled, (state, action) => {
      state.postForgotPasswordResponse = apiSliceBuilderService(
        BUILDER_TYPE_FULFILLED,
        action
      );
      const { loginToken = "" } = action.payload.data.data;
      const decodedToken = parseJwt(loginToken);
      state.loginTokenValues = decodedToken;
      state.loginToken = loginToken;
    });

    builder.addCase(postNewPassword.pending, (state) => {
      state.postNewPasswordResponse =
        apiSliceBuilderService(BUILDER_TYPE_PENDING);
    });
    builder.addCase(postNewPassword.rejected, (state, action) => {
      if (action?.payload?.status === RESPONSE_CODE_406) {
        state.postNewPasswordResponse = apiSliceBuilderService(
          BUILDER_TYPE_REJECTED,
          action
        );
      }
    });
    builder.addCase(postNewPassword.fulfilled, (state, action) => {
      state.postNewPasswordResponse = apiSliceBuilderService(
        BUILDER_TYPE_FULFILLED,
        action
      );
    });

    builder.addCase(postResetForgotPassword.pending, (state) => {
      state.postResetForgotPasswordResponse =
        apiSliceBuilderService(BUILDER_TYPE_PENDING);
    });
    builder.addCase(postResetForgotPassword.rejected, (state, action) => {
      if (action?.payload?.status === RESPONSE_CODE_406) {
        state.postResetForgotPasswordResponse = apiSliceBuilderService(
          BUILDER_TYPE_REJECTED,
          action
        );
      }
    });
    builder.addCase(postResetForgotPassword.fulfilled, (state, action) => {
      state.postResetForgotPasswordResponse = apiSliceBuilderService(
        BUILDER_TYPE_FULFILLED,
        action
      );
    });
    builder.addCase(postResetPassword.pending, (state) => {
      state.postResetPasswordResponse =
        apiSliceBuilderService(BUILDER_TYPE_PENDING);
    });
    builder.addCase(postResetPassword.rejected, (state, action) => {
      if (action?.payload?.status === RESPONSE_CODE_406) {
        state.postResetPasswordResponse = apiSliceBuilderService(
          BUILDER_TYPE_REJECTED,
          action
        );
      }
    });
    builder.addCase(postResetPassword.fulfilled, (state, action) => {
      state.postResetPasswordResponse = apiSliceBuilderService(
        BUILDER_TYPE_FULFILLED,
        action
      );
    });
    builder.addCase(postIdentifyUser.pending, (state) => {
      state.postIdentifyUserResponse =
        apiSliceBuilderService(BUILDER_TYPE_PENDING);
    });
    builder.addCase(postIdentifyUser.rejected, (state, action) => {
      if (action?.payload?.status === RESPONSE_CODE_406) {
        state.postIdentifyUserResponse = apiSliceBuilderService(
          BUILDER_TYPE_REJECTED,
          action
        );
        return;
      }
      throw new Error(action?.payload?.data?.error);
    });
    builder.addCase(postIdentifyUser.fulfilled, (state, action) => {
      state.postIdentifyUserResponse = apiSliceBuilderService(
        BUILDER_TYPE_FULFILLED,
        action
      );
      const { loginToken } = action.payload.data.data;
      state.loginToken = loginToken;
      const decodedToken = parseJwt(loginToken);
      state.loginTokenValues = decodedToken;
    });
    builder.addCase(postRemoveVerificationMethod.pending, (state) => {
      state.postRemoveVerificationMethodResponse =
        apiSliceBuilderService(BUILDER_TYPE_PENDING);
    });
    builder.addCase(postRemoveVerificationMethod.rejected, (state, action) => {
      state.postRemoveVerificationMethodResponse = apiSliceBuilderService(
        BUILDER_TYPE_REJECTED,
        action
      );
    });
    builder.addCase(postRemoveVerificationMethod.fulfilled, (state, action) => {
      state.postRemoveVerificationMethodResponse = apiSliceBuilderService(
        BUILDER_TYPE_FULFILLED,
        action
      );
      state.toastFlow = REMOVE_TOAST;
      if (action.meta.arg?.verificationMethod === PHONE) {
        state.phoneNo = EMPTY;
        state.loginTokenValues.pvm = EMAIL;
      } else {
        state.email = EMPTY;
        state.loginTokenValues.pvm = PHONE;
      }
    });
  },
});
export const {
  resetLoginMockSmsOtp,
  resetIsUsernameRetrieved,
  resetLoginValidateOTPDescription,
  resetIsUserVerificationRemembered,
  resetLoginResponseDescription,
  clearLoginApiResponse,
  clearLoginSlice,
  saveForgotUsernameData,
  saveForgotPasswordData,
  saveVerificationMethodsData,
  logout,
  updateAddEditVerificationValues,
  resetAddEditVerificationValues,
  resetLoginSendOTPDescription,
  resetToastFlow,
  setIsAddEditVerificationFlow,
  resetIsAddEditVerificationFlow,
} = loginSlice.actions;

export default loginSlice.reducer;
